import { flexRender, type Row } from '@tanstack/react-table';
import { type KeyboardEvent, type ReactNode } from 'react';

import { cx } from '@/lib/cx';
import { Skeleton } from '@/ui/primitives/skeleton';

/** Metadado opcional da coluna: alinhamento, largura e o campo de ordenação no servidor. */
export interface MetaColuna {
  alinhar?: 'direita';
  largura?: string;
  /** Campo aceito pelo `ordenarPor` da lista (só na TabelaPaginada). */
  ordenavel?: string;
}

export interface SituacaoProps {
  carregando: boolean;
  vazia: boolean;
  colunas: number;
  vazio: ReactNode;
}

/** Linhas de esqueleto enquanto carrega; o estado vazio quando não há nada. */
export function Situacao({ carregando, vazia, colunas, vazio }: SituacaoProps) {
  if (!vazia) return null;
  if (carregando) return <LinhasCarregando colunas={colunas} />;
  return (
    <tr>
      <td colSpan={colunas}>{vazio}</td>
    </tr>
  );
}

/** O que a linha sabe fazer: abrir (clique/Enter), marcar a selecionada, ações no hover. */
export interface ComportamentoLinha<T> {
  aoAbrir?: ((linha: T, elemento: HTMLElement) => void) | undefined;
  acoesLinha?: ((linha: T) => ReactNode) | undefined;
  selecionadaId?: string | null | undefined;
}

export function Linha<T>({ linha, props }: { linha: Row<T>; props: ComportamentoLinha<T> }) {
  const { aoAbrir, acoesLinha, selecionadaId } = props;
  const abrir = (elemento: HTMLElement) => aoAbrir?.(linha.original, elemento);
  const teclado = (evento: KeyboardEvent<HTMLTableRowElement>) => {
    if (evento.key === 'Enter' && evento.target === evento.currentTarget)
      abrir(evento.currentTarget);
  };
  return (
    <tr
      tabIndex={aoAbrir ? 0 : undefined}
      onClick={
        aoAbrir
          ? (evento) => {
              abrir(evento.currentTarget);
            }
          : undefined
      }
      onKeyDown={aoAbrir ? teclado : undefined}
      aria-selected={selecionadaId === linha.id || undefined}
      className={cx(
        'group h-linha transition-colors duration-[var(--duracao-rapida)]',
        aoAbrir && 'cursor-pointer hover:bg-superficie-hover focus-visible:bg-superficie-hover',
        'aria-selected:bg-superficie-selecao',
      )}
    >
      {linha.getVisibleCells().map((celula) => {
        const meta = celula.column.columnDef.meta as MetaColuna | undefined;
        return (
          <td
            key={celula.id}
            className={cx(
              'border-b border-borda px-3 py-1.5 align-middle',
              meta?.alinhar === 'direita' && 'text-right',
            )}
          >
            {flexRender(celula.column.columnDef.cell, celula.getContext())}
          </td>
        );
      })}
      {acoesLinha && (
        <td className="border-b border-borda px-2 text-right">
          <div
            className="flex justify-end gap-1 opacity-0 transition-opacity duration-[var(--duracao-rapida)] group-hover:opacity-100 group-focus-within:opacity-100"
            onClick={(evento) => {
              evento.stopPropagation();
            }}
          >
            {acoesLinha(linha.original)}
          </div>
        </td>
      )}
    </tr>
  );
}

export function LinhasCarregando({ colunas, linhas = 8 }: { colunas: number; linhas?: number }) {
  return Array.from({ length: linhas }, (_, i) => (
    <tr key={i} className="h-linha">
      {Array.from({ length: colunas }, (__, j) => (
        <td key={j} className="border-b border-borda px-3">
          <Skeleton className="h-3.5 w-3/4" />
        </td>
      ))}
    </tr>
  ));
}
